import {
  AnyVideoType,
  OrderVideoInfo,
} from '../../appComponents/anyVideo/types';
import {
  AccountItem,
  BaseProfileItem,
  BaseStoreListingItem,
  BaseUpcomingShowItem,
  BaseUpcomingShowItemPermissionEnum,
  DiscoveryStoreListingItem,
  LiveStreamItem,
  LiveStreamItemPermissionEnum,
  LiveStreamItemShippingModeEnum,
  LiveStreamItemWithId,
  LiveStreamUserItem,
  MessageItemWithId,
  MyStoreListingItem,
  OrderItem,
  OrderProfileItem,
  OurPicksForYouItem,
  ProfileItem,
  ProfilesFollowerItem,
  RichOrderItem,
  SearchLiveStreamItem,
  ShowEventWithoutMetaDataItem,
  StoreFrontItemWithBaseProfile,
  StoreListingItem,
  StoreListingItemWithMetadata,
  TopReviewListingItem,
  UpcomingShowItem,
} from '../../generated/api';
import {
  Listing,
  LiveStream,
  LiveStreamUser,
  MessagePurpose,
  Order,
  Profile,
  ProfileUserType,
  ShippingMode,
  ShowEvent,
  ShowPermission,
  StoreFront,
  UpcomingShow,
  VisibilityType,
} from '../../models/ClientModels';
import { kPrivateEmailPlaceholder } from '../constants';
import { Timestamp } from '../firebase/firebaseAccess';
import { WithID } from '../firebase/rxfireExtensions';
import {
  apiServerProductsListingStatusEnum,
  apiServerStoreListingOriginalInfoTypeToListingOriginType,
  apiServerStoreListingVariantToListingVariant,
} from '../listing/types';

export function profileItemToProfile(
  profileItem: ProfileItem | BaseProfileItem | OrderProfileItem,
): WithID<Profile & { nftImageUrl?: string }> {
  // ProfileItem is compatible with Profile with the following missing:
  // - badges, bio, gifting.recipient.nftImageUrl
  const { createdAt, ...profile } = profileItem;
  return {
    ...(profile as any),
    email:
      'email' in profileItem ? profileItem.email : kPrivateEmailPlaceholder,
    createdAt: timestampFromDate(createdAt),
  };
}

type FirebaseTimestampLike = { seconds: number; nanoseconds: number };

type BigQueryTimestamp = { seconds: number; nanos: number };

export function isFirebaseTimestampObject(
  value: any,
): value is FirebaseTimestampLike {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'seconds' in value &&
      'nanoseconds' in value,
  ) as any;
}

export function isFirebaseConvertibleTimestampObjectFromBigQuery(
  value: any,
): value is BigQueryTimestamp {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'seconds' in value &&
      'nanos' in value,
  ) as any;
}

export function timestampFromDate<
  T extends string | Date | Timestamp | null | undefined,
>(
  date: T,
  fallbackOnError: (date: T) => Timestamp = () =>
    Timestamp.fromDate(new Date()),
): T extends string | Date ? Timestamp : Timestamp | undefined {
  try {
    if (!date) {
      return undefined as any;
    }
    if (date instanceof Timestamp) return date;

    if (isFirebaseTimestampObject(date)) {
      return new Timestamp(date.seconds, date.nanoseconds);
    }

    if (isFirebaseConvertibleTimestampObjectFromBigQuery(date)) {
      return new Timestamp(date.seconds, date.nanos);
    }

    let dateValue: Date | undefined = undefined;
    try {
      if (typeof date === 'string' && /^\d+$/.test(date)) {
        dateValue = new Date(Number(date));
      } else if (date) {
        dateValue = new Date(date);
      }
    } catch (e) {
      console.error(e);
    }

    return (dateValue ? Timestamp.fromDate(dateValue) : undefined) as any;
  } catch (e) {
    console.error(
      `error occurred while attempting to convert date value ${JSON.stringify(
        date,
      )} to firebase timestamp`,
      e,
    );
    try {
      return fallbackOnError(date);
    } catch (e) {
      return undefined as any;
    }
  }
}

export function orderItemToOrder(
  orderItem: RichOrderItem | OrderItem,
): WithID<Order> {
  const {
    createdAt,
    pickupNotifiedAt,
    planTimeOfShippingLabelGeneration,
    firstPurchaseAt,
    seller,
    buyer,
    gifting,
    ...order
  } = orderItem;
  return {
    ...(order as any),
    createdAt: timestampFromDate(createdAt),
    pickupNotifiedAt: timestampFromDate(pickupNotifiedAt),
    planTimeOfShippingLabelGeneration: timestampFromDate(
      planTimeOfShippingLabelGeneration,
    ),
    firstPurchaseAt: timestampFromDate(firstPurchaseAt),
    seller: profileItemToProfile(seller),
    buyer: profileItemToProfile(buyer),
    gifting: gifting
      ? {
          recipientId: gifting.recipientId,
          recipient: profileItemToProfile(gifting.recipient as any),
          status: gifting.status as any,
        }
      : undefined,
  };
}

export function messageItemToMessage(item: MessageItemWithId): OrderVideoInfo {
  const purpose = item.purpose ?? MessagePurpose.review;
  const author = {
    ...profileItemToProfile(item.userProfileFrom),
    id: item.userIdFrom,
  };
  return {
    // common properties
    key: item.id,
    id: item.id,
    author,
    messageId: item.id,
    videoURL: item.videoURL,
    originalVideoURL: item.originalVideoURL,
    gifURL: item.gifURL,
    thumbnailURL: item.thumbnailURL,
    available: item.available,
    visibility: item.visibility as any,
    createdAt: timestampFromDate(item.createdAt),

    // associated listing information
    listings: item.order?.listingId
      ? [
          {
            id: item.order?.listingId,
            ...(item.order?.listingSnapshot as any),
          },
        ]
      : undefined,
    variantId: item.order?.variantId,

    // order video specific properties
    orderId: item.orderId,
    transcodeStatus: item.transcodeStatus as any,
    from: author,
    to: {
      ...profileItemToProfile(item.userProfileTo),
      id: item.userIdTo,
    },

    ...(purpose === MessagePurpose.review
      ? {
          type: AnyVideoType.Review,
          emoji: item.emoji as any,
          upvoteCount: item.upvoteCount,
        }
      : {
          type: AnyVideoType.ThankYou,
          thankYouType: item.thankYouType as any,
          sellerId: item.sellerId,
        }),
  };
}

export function profileFollowerItemToProfile(item: ProfilesFollowerItem) {
  return profileItemToProfile(item as any);
}

export function accountItemToProfile(item: AccountItem): WithID<Profile> & {
  liveStreamId?: string;
  upcomingShowId?: string;
} {
  const { liveStreamId, upcomingShowId, ...profile } = item;
  return {
    ...(profile as any),
    liveStreamId,
    upcomingShowId,
  };
}

export function liveStreamUserConverted<
  T extends LiveStreamUserItem | undefined,
>(item: T): T extends undefined ? LiveStreamUser | undefined : LiveStreamUser {
  if (item == null) return undefined as any;
  return {
    userId: item.userId,
    profile: profileItemToProfile(item.profile),
    device: item.device,
    lastConnect: timestampFromDate(item.lastConnect),
    status: item.status,
  };
}

export const upcomingShowItemConverted = (
  item: UpcomingShowItem | BaseUpcomingShowItem,
): WithID<UpcomingShow> => {
  const { user, startsAt, updatedAt, createdAt, ...props } = item;
  return {
    ...(props as any),
    user: profileItemToProfile(user),
    startsAt: timestampFromDate(startsAt),
    updatedAt: timestampFromDate(updatedAt),
    createdAt: timestampFromDate(createdAt),
  };
};

export const liveStreamItemConverted = (
  item: LiveStreamItemWithId,
): WithID<LiveStream> => {
  const {
    primary,
    secondary,
    upcomingShow,
    createdAt,
    disconnectedAt,
    ...props
  } = item;
  return {
    ...(props as any),
    primary: liveStreamUserConverted(primary),
    secondary: liveStreamUserConverted(secondary),
    createdAt: timestampFromDate(createdAt),
    disconnectedAt: timestampFromDate(disconnectedAt),
    upcomingShow: upcomingShow
      ? upcomingShowItemConverted(upcomingShow)
      : undefined,
  };
};

export const storefrontItemConverted = (
  item: StoreFrontItemWithBaseProfile,
  profileFallback?: ProfileItem,
): WithID<StoreFront> => {
  const {
    createdAt,
    profile = profileFallback,
    liveStream,
    upcomingShow,
    ...props
  } = item;
  return {
    ...(props as any),
    createdAt: timestampFromDate(createdAt),
    profile: profile ? profileItemToProfile(profile) : undefined,
    liveStream: liveStream
      ? liveStreamItemConverted(liveStream as LiveStreamItemWithId)
      : undefined,
    upcomingShow: upcomingShow
      ? upcomingShowItemConverted(upcomingShow)
      : undefined,
  };
};

export function storeListingItemToListing(
  l:
    | WithID<StoreListingItem>
    | WithID<StoreListingItemWithMetadata>
    | WithID<MyStoreListingItem>
    | WithID<BaseStoreListingItem>
    | WithID<TopReviewListingItem>,
  overrides?: Partial<Listing>,
): WithID<Listing> {
  return {
    ...l,
    type: 'type' in l ? (l.type as any) : undefined,
    discountStartDate:
      'discountStartDate' in l
        ? timestampFromDate(l.discountStartDate)
        : undefined,
    discountEndDate:
      'discountEndDate' in l ? timestampFromDate(l.discountEndDate) : undefined,
    moderationStatus:
      'moderationStatus' in l ? (l.moderationStatus as any) : undefined,
    moderatedAt:
      'moderatedAt' in l ? timestampFromDate(l.moderatedAt) : undefined,
    rating: 'rating' in l ? (l.rating as any) : undefined,
    dropshipDetails:
      'dropshipDetails' in l ? (l.dropshipDetails as any) : undefined,
    dims: l.dims as any,
    weight: l.weight as any,
    shippingSurcharge:
      'shippingSurcharge' in l ? (l.shippingSurcharge as any) : undefined,
    createdAt: l.createdAt == null ? undefined : timestampFromDate(l.createdAt),
    variants: l.variants?.map(apiServerStoreListingVariantToListingVariant),
    status:
      l.status == null
        ? undefined
        : apiServerProductsListingStatusEnum(l.status as any),
    originalInfo:
      l.originalInfo == null
        ? undefined
        : {
            ...l.originalInfo,
            type: apiServerStoreListingOriginalInfoTypeToListingOriginType(
              l.originalInfo.type,
            ),
          },
    meeting:
      'meeting' in l && l.meeting != null
        ? {
            ...l.meeting,
            startDate: timestampFromDate(l.meeting.startDate),
            endDate: timestampFromDate(l.meeting.endDate),
            repeatFrequency: type_erased(l.meeting.repeatFrequency),
            availableDates: l.meeting.availableDates?.map(date => Number(date)),
          }
        : undefined,
    ...overrides,
  };
}

function apiServerLiveStreamItemPermissionEnumToShowPermission(
  item: LiveStreamItemPermissionEnum,
): ShowPermission {
  return item as any;
}

export function apiServerLiveStreamToLiveStream(
  item: LiveStreamItem | SearchLiveStreamItem,
): LiveStream {
  return {
    ...item,
    startedAt: timestampFromDate(item.startedAt),
    createdAt: timestampFromDate(item.createdAt),
    disconnectedAt: timestampFromDate(item.disconnectedAt),
    wrapUpEmailSentAt: timestampFromDate(item.wrapUpEmailSentAt),
    primary: apiServerLiveStreamUserToLiveStreamUser(item.primary),
    secondary:
      item.secondary == null
        ? item.secondary
        : apiServerLiveStreamUserToLiveStreamUser(item.secondary),
    upcomingShow:
      item.upcomingShow == null
        ? item.upcomingShow
        : apiServerUpcomingShowToUpcomingShow(item.upcomingShow),
    permission:
      item.permission == null
        ? item.permission
        : apiServerLiveStreamItemPermissionEnumToShowPermission(
            item.permission as any,
          ),
    shippingMode: item.shippingMode?.map(mode =>
      mode === LiveStreamItemShippingModeEnum.Shipping
        ? ShippingMode.Shipping
        : ShippingMode.Pickup,
    ),
    moderationStatus: item.moderationStatus as any,
    moderatedBy: item.moderatedBy as any,
    moderatedAt: timestampFromDate(item.moderatedAt),
    accessLevel: undefined,
  };
}

function apiServerLiveStreamUserToLiveStreamUser(
  item: LiveStreamUserItem,
): LiveStreamUser {
  return {
    ...item,
    profile: profileItemToProfile(item.profile),
    lastConnect: timestampFromDate(item.lastConnect),
  };
}

function apiServerUpcomingShowItemPermissionEnumToShowPermission(
  item: BaseUpcomingShowItemPermissionEnum,
): ShowPermission {
  return item as any;
}
export function apiServerUpcomingShowToUpcomingShow(
  item: BaseUpcomingShowItem,
): UpcomingShow {
  return {
    ...item,
    user: profileItemToProfile(item.user),
    visibility: item.visibility as any,
    permission:
      item.permission == null
        ? item.permission
        : apiServerUpcomingShowItemPermissionEnumToShowPermission(
            item.permission,
          ),
    createdAt: timestampFromDate(item.createdAt),
    updatedAt: timestampFromDate(item.updatedAt),
    startsAt: timestampFromDate(item.startsAt),
    accessLevel: undefined,
  };
}

export function apiServerBaseShowEventItemToShowEvent(
  item: ShowEventWithoutMetaDataItem,
): ShowEvent {
  return {
    ...item,
    createdAt: timestampFromDate(item.createdAt),
    startedAfter: timestampFromDate(item.startedAfter),
    endedBefore: timestampFromDate(item.endedBefore),
    sortedAt: timestampFromDate(item.sortedAt),
  };
}

export function apiServerOurPicksForYouToStoreFront(
  item: OurPicksForYouItem,
): WithID<StoreFront> {
  const upcomingShow: UpcomingShow | undefined = item.upcomingShow
    ? {
        ...item.upcomingShow,
        createdAt: timestampFromDate(item.upcomingShow.createdAt),
        updatedAt: timestampFromDate(item.upcomingShow.updatedAt),
        startsAt: timestampFromDate(item.upcomingShow.startsAt),
        user: {
          ...item.upcomingShow.user,
          email: kPrivateEmailPlaceholder,
          createdAt: timestampFromDate(item.upcomingShow.user.createdAt),
          userType: item.upcomingShow.user.userType as ProfileUserType,
          storeType: item.upcomingShow.user.storeType as any,
        },
        permission: item.upcomingShow.permission as unknown as ShowPermission,
        visibility: item.upcomingShow.visibility as VisibilityType,
        accessLevel: undefined,
      }
    : undefined;
  const liveStream = item.liveStream!;

  return {
    ...item,
    // openBoxMode: item.openBoxMode as any,
    // weekTimeOfShippingLabelGeneration:
    //   item.weekTimeOfShippingLabelGeneration as any,
    createdAt: timestampFromDate(item.createdAt),
    profile: {
      ...item.profile,
      email: kPrivateEmailPlaceholder,
      createdAt: timestampFromDate(item.profile.createdAt),
      userType: item.profile.userType as ProfileUserType,
      storeType: item.profile.storeType as any,
    },
    upcomingShow,
    liveStream: liveStream as LiveStream,
    lastLiveStreamDate: timestampFromDate(item.lastLiveStreamDate),
  };
}

export function storeListingItemWithMetadataToListing(
  l: StoreListingItemWithMetadata,
): WithID<Listing> {
  return {
    ...l,
    type: l.type as any,
    discountStartDate: timestampFromDate(l.discountStartDate),
    discountEndDate: timestampFromDate(l.discountEndDate),
    moderationStatus: l.moderationStatus as any,
    moderatedAt: timestampFromDate(l.moderatedAt),
    rating: l.rating as any,
    dropshipDetails: l.dropshipDetails as any,
    dims: l.dims as any,
    weight: l.weight as any,
    shippingSurcharge: l.shippingSurcharge as any,
    createdAt: l.createdAt == null ? undefined : timestampFromDate(l.createdAt),
    variants: l.variants?.map(apiServerStoreListingVariantToListingVariant),
    status:
      l.status == null
        ? undefined
        : apiServerProductsListingStatusEnum(l.status as any),
    originalInfo:
      l.originalInfo == null
        ? undefined
        : {
            ...l.originalInfo,
            type: apiServerStoreListingOriginalInfoTypeToListingOriginType(
              l.originalInfo.type,
            ),
          },
    meeting:
      l.meeting != null
        ? {
            ...l.meeting,
            startDate: timestampFromDate(l.meeting.startDate),
            endDate: timestampFromDate(l.meeting.endDate),
            repeatFrequency: type_erased(l.meeting.repeatFrequency),
            availableDates: l.meeting.availableDates?.map(date => Number(date)),
          }
        : undefined,
  };
}

export function discoveryStoreListingItemToListing(
  l: DiscoveryStoreListingItem,
): WithID<Listing> {
  return {
    ...l,
    type: l.type as any,
    discountStartDate: timestampFromDate(l.discountStartDate),
    discountEndDate: timestampFromDate(l.discountEndDate),
    moderationStatus: l.moderationStatus as any,
    moderatedAt: timestampFromDate(l.moderatedAt),
    rating: l.rating as any,
    dropshipDetails: l.dropshipDetails as any,
    dims: l.dims as any,
    weight: l.weight as any,
    shippingSurcharge: l.shippingSurcharge as any,
    createdAt: l.createdAt == null ? undefined : timestampFromDate(l.createdAt),
    variants: l.variants?.map(apiServerStoreListingVariantToListingVariant),
    status:
      l.status == null
        ? undefined
        : apiServerProductsListingStatusEnum(l.status as any),
    originalInfo:
      l.originalInfo == null
        ? undefined
        : {
            ...l.originalInfo,
            type: apiServerStoreListingOriginalInfoTypeToListingOriginType(
              l.originalInfo.type,
            ),
          },
    meeting:
      l.meeting != null
        ? {
            ...l.meeting,
            startDate: timestampFromDate(l.meeting.startDate),
            endDate: timestampFromDate(l.meeting.endDate),
            repeatFrequency: type_erased(l.meeting.repeatFrequency),
            availableDates: l.meeting.availableDates?.map(date => Number(date)),
          }
        : undefined,
  };
}

export const type_erased = <T, U>(x: T): U => x as unknown as U;
